import { useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type AiModelResponse,
  adminDeleteModelMutation,
  adminListModelReferencesOptions,
  adminUpdateModelMutation,
  adminUpdateTierDefaultMutation,
  errorMessage,
  type ModelReferencesResponse,
  type ModelTierDefaultResponse,
} from "@/api";
import { type ReassignAction, ReassignDialog } from "./ReassignDialog";
import { useRefreshModels } from "./useModelData";

interface Pending {
  model: AiModelResponse;
  action: ReassignAction;
  references: ModelReferencesResponse;
  resolve: (confirmed: boolean) => void;
}

/**
 * Model mutations shared by the list and configure pages. Disabling an in-use
 * model and deleting any model go through the reassign/confirm dialog first
 * (returned as `dialog`, render it once).
 */
export function useModelActions(models: AiModelResponse[]) {
  const { t } = useTranslation("adminModels");
  const toast = useToast();
  const queryClient = useQueryClient();
  const refresh = useRefreshModels();
  const [pending, setPending] = useState<Pending | null>(null);
  const busy = useRef(false);
  const update = useMutation(adminUpdateModelMutation());
  const remove = useMutation(adminDeleteModelMutation());
  const setTier = useMutation(adminUpdateTierDefaultMutation());

  /** Resolves true when the action may go ahead (nothing in use, or usage moved / delete confirmed). */
  const confirmReassign = async (model: AiModelResponse, action: ReassignAction): Promise<boolean> => {
    // A second dialog would replace the first one's resolver and strand its caller.
    if (busy.current) return false;
    busy.current = true;
    try {
      const { data: references } = await queryClient.fetchQuery({
        ...adminListModelReferencesOptions({ path: { model_id: model.id } }),
        staleTime: 0,
      });
      const inUse = (references.affectedAgents?.length ?? 0) > 0 || (references.chatCount ?? 0) > 0;
      if (!inUse && action !== "delete") return true;
      return await new Promise<boolean>((resolve) => setPending({ model, action, references, resolve }));
    } catch (error) {
      toast.error(errorMessage(error, t("reassign.loadFailed")));
      return false;
    } finally {
      busy.current = false;
    }
  };

  const settle = (confirmed: boolean) => {
    pending?.resolve(confirmed);
    setPending(null);
    if (confirmed) void refresh();
  };

  const setEnabled = async (model: AiModelResponse, enabled: boolean) => {
    if (!enabled && !(await confirmReassign(model, "disable"))) return;
    update.mutate(
      { path: { model_id: model.id }, body: { isEnabled: enabled } },
      {
        onSuccess: () => {
          toast.success(t(enabled ? "actions.enabled" : "actions.disabled", { name: model.name }));
          void refresh();
        },
        onError: (error) => toast.error(errorMessage(error, t("actions.updateFailed"))),
      },
    );
  };

  const deleteModel = async (model: AiModelResponse, onDeleted?: () => void) => {
    if (!(await confirmReassign(model, "delete"))) return;
    remove.mutate(
      { path: { model_id: model.id } },
      {
        onSuccess: () => {
          toast.success(t("actions.deleted", { name: model.name }));
          void refresh();
          onDeleted?.();
        },
        onError: (error) => toast.error(errorMessage(error, t("actions.deleteFailed"))),
      },
    );
  };

  const setDefault = (model: AiModelResponse, tier: ModelTierDefaultResponse) =>
    setTier.mutate(
      { path: { tier: tier.tier }, body: { modelId: model.id } },
      {
        onSuccess: () => {
          toast.success(t("actions.defaultSet", { name: model.name, tier: tier.label }));
          void refresh();
        },
        onError: (error) => toast.error(errorMessage(error, t("defaults.saveFailed"))),
      },
    );

  const move = (model: AiModelResponse) => void confirmReassign(model, "move");

  const dialog = pending && (
    <ReassignDialog
      model={pending.model}
      models={models}
      action={pending.action}
      references={pending.references}
      onClose={() => settle(false)}
      onDone={() => settle(true)}
    />
  );

  return { setEnabled, deleteModel, setDefault, move, confirmReassign, dialog, deleting: remove.isPending };
}
